import { useState, useEffect, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { useTheme } from "@/contexts/ThemeContext";
import { useJobCommsWebSocket } from "@/contexts/JobCommsWebSocketContext";
import JobCommsChat from "@/components/JobCommsChat";
import JobCommsDocuments from "@/components/JobCommsDocuments";
import JobCommsJobDetails from "@/components/JobCommsJobDetails";
import CompleteJobModal from "@/components/CompleteJobModal";
import {
  ArrowLeft, MessageSquare, FileText, Info, Loader2, MapPin, Calendar,
  CheckCircle, DollarSign, Users, X, Lock, Check, MoreHorizontal, Star
} from "lucide-react";
import "@/styles/commandDeck.css";

const statusColors = {
  HIRED: "bg-blue-500",
  PARTIALLY_HIRED: "bg-blue-400",
  PENDING_COMPLETION: "bg-purple-500",
  RELEASE_PAYMENT: "bg-green-500",
};

const statusLabels = {
  HIRED: "Confirmed",
  PARTIALLY_HIRED: "Partially Hired",
  PENDING_COMPLETION: "Pending Completion",
  RELEASE_PAYMENT: "Awaiting Payment",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "TBD";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
};

const formatMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isJobDatePassed = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr + "T00:00:00") <= today;
};

// Mobile tabs
const tabs = [
  { id: "details", label: "Details", icon: Info },
  { id: "chat", label: "Chat", icon: MessageSquare },
  { id: "documents", label: "Docs", icon: FileText },
];

// Status -> pill colour map (ink + translucent bg)
const statusPill = {
  HIRED: { ink: "#22D3EE", bg: "rgba(14,116,144,.25)" },
  PARTIALLY_HIRED: { ink: "#22D3EE", bg: "rgba(14,116,144,.25)" },
  PENDING_COMPLETION: { ink: "#C084FC", bg: "rgba(126,34,206,.25)" },
  RELEASE_PAYMENT: { ink: "#67C6B4", bg: "rgba(21,128,61,.25)" },
};

const JobCommsDetail = ({ onLogout }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const ws = useJobCommsWebSocket();
  const user = JSON.parse(localStorage.getItem("shootzu_user") || "{}");

  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [jobData, setJobData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [userRole, setUserRole] = useState("crew");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCrewSelector, setShowCrewSelector] = useState(false);
  const [selectedCrewForReview, setSelectedCrewForReview] = useState(null);

  const fetchJobCommsDetail = useCallback(async () => {
    if (!user.email || !jobId) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${jobId}?user_email=${user.email}`
      );
      setJobData(res.data.job);
      setConversations(res.data.conversations || []);
      setDocuments(res.data.documents || []);
      setUserRole(res.data.user_role || "crew");
    } catch (err) {
      console.error("Failed to load job comms detail:", err);
    } finally {
      setLoading(false);
    }
  }, [user.email, jobId]);

  useEffect(() => { fetchJobCommsDetail(); window.scrollTo(0, 0); }, [fetchJobCommsDetail]);

  useEffect(() => {
    if (ws && jobId) {
      ws.joinJob(jobId);
      return () => ws.leaveJob(jobId);
    }
  }, [ws, jobId]);

  if (loading) {
    return (
      <div className="cd-root h-screen overflow-hidden">
        <Sidebar onLogout={onLogout} />
        <Header />
        <div className="sidebar-content h-full flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#28C0D0" }} />
        </div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="cd-root h-screen overflow-hidden">
        <Sidebar onLogout={onLogout} />
        <Header />
        <div className="sidebar-content h-full flex flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            <p className="text-lg" style={{ color: "rgba(255,255,255,.6)" }}>Job not found</p>
            <button
              onClick={() => navigate("/job-comms")}
              className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ background: "#28C0D0", color: "#0C1120" }}
            >
              Back to Job Comms
            </button>
          </div>
        </div>
      </div>
    );
  }

  const unreadCount = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const datePassed = isJobDatePassed(jobData.date);
  const isAdvanced = jobData.is_advanced_job;
  const activeCrew = (jobData.crew_members || []).filter(cm => cm.hired);
  const allCrewPaid = activeCrew.length > 0 && activeCrew.every(cm => cm.payment_released);
  const canRelease = userRole === "business" && datePassed && ["HIRED", "PENDING_COMPLETION"].includes(jobData.status) && !allCrewPaid;

  const escrowAmount =
    activeCrew.reduce((s, cm) => s + (Number(cm.subtotal) || 0), 0) || Number(jobData.total_amount) || 0;

  const handleReleaseFAB = () => {
    if (isAdvanced && activeCrew.length > 1) {
      setShowCrewSelector(true);
    } else {
      setShowCompleteModal(true);
    }
  };

  const handleSelectCrewForReview = (crew) => {
    setSelectedCrewForReview(crew);
    setShowCrewSelector(false);
    setShowCompleteModal(true);
  };

  // Progress stepper state
  const steps = [
    { label: "Confirmed", sub: "Booked", state: "complete" },
    { label: "Shoot day", sub: formatDate(jobData.date), state: datePassed ? "complete" : "current" },
    { label: "Wrap up", sub: allCrewPaid ? "Done" : "Awaiting you", state: allCrewPaid ? "complete" : (datePassed ? "current" : "pending") },
    { label: "Paid", sub: `${formatMoney(escrowAmount)} escrow`, state: allCrewPaid ? "complete" : "pending" },
  ];

  const PanelHeader = ({ icon: Icon, label, badge }) => (
    <div
      className="flex items-center justify-between px-4 py-3 flex-shrink-0"
      style={{ background: "rgba(255,255,255,.03)", borderBottom: "1px solid rgba(255,255,255,.07)" }}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: "rgba(40,192,208,.14)" }}
        >
          <Icon className="w-4 h-4" style={{ color: "#28C0D0" }} />
        </div>
        <span className="text-sm font-bold text-white">{label}</span>
      </div>
      {badge > 0 && (
        <span
          className="min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-bold rounded-full px-1.5"
          style={{ background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.6)" }}
        >
          {badge}
        </span>
      )}
    </div>
  );

  const escrowHero = (
    <div
      style={{
        borderRadius: 16,
        padding: "18px 20px",
        background: "linear-gradient(135deg,rgba(21,128,61,.22),rgba(15,118,110,.1))",
        border: "1px solid rgba(103,198,180,.28)",
      }}
    >
      <div className="flex items-center justify-between">
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".6px", color: "#67C6B4" }}>IN ESCROW</span>
        <Lock className="w-4 h-4" style={{ color: "#67C6B4" }} />
      </div>
      <div style={{ fontFamily: "Poppins, 'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 30, color: "#fff", letterSpacing: "-.8px", marginTop: 6, lineHeight: 1 }}>
        {formatMoney(escrowAmount)}
      </div>
      <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.55)", marginTop: 5 }}>Released to crew on completion</div>
    </div>
  );

  return (
    <div className="cd-root h-screen overflow-hidden">
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="sidebar-content h-full flex flex-col" style={{ position: "relative", zIndex: 1 }}>
        {/* Page-scoped responsive rules (prototype stacks the 3-col body at 1080px) */}
        <style>{`
          .jc-body { flex: 1; min-height: 0; display: flex; }
          @media (max-width: 1080px) {
            .jc-body { flex-direction: column; overflow-y: auto; }
            .jc-body .sz-panel-m, .jc-body .sz-chat-m {
              width: 100%; flex: 0 0 auto; border-right: none;
            }
            .jc-body .sz-chat-m { min-height: 62vh; }
          }
          @media (prefers-reduced-motion: reduce) {
            .jc-pulse { animation: none !important; }
          }
          /* Subtle dark scrollbars — override the global purple thumb on the
             overflow columns so no bright vertical bars appear between panels. */
          .jc-body, .jc-body * { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,.18) transparent; }
          .jc-body::-webkit-scrollbar,
          .jc-body ::-webkit-scrollbar { width: 8px; height: 8px; }
          .jc-body::-webkit-scrollbar-track,
          .jc-body ::-webkit-scrollbar-track { background: transparent; }
          .jc-body::-webkit-scrollbar-thumb,
          .jc-body ::-webkit-scrollbar-thumb { background: rgba(255,255,255,.12); border-radius: 8px; }
          .jc-body::-webkit-scrollbar-thumb:hover,
          .jc-body ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,.2); }
        `}</style>
        {/* Hero Header */}
        <div
          className="flex-shrink-0"
          style={{
            padding: "16px 28px 0",
            borderBottom: "1px solid rgba(255,255,255,.07)",
            background: "linear-gradient(120deg,rgba(103,198,180,.09),rgba(40,192,208,.03))",
          }}
        >
          {/* Back breadcrumb */}
          <button
            onClick={() => navigate("/job-comms")}
            className="inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
            style={{ color: "rgba(255,255,255,.6)", marginBottom: 14 }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Job Comms
          </button>

          {/* Title row */}
          <div className="sz-row flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-white" style={{ fontFamily: "Poppins, 'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 23, letterSpacing: "-.5px" }}>
                  {jobData.title}
                </h1>
                <span
                  className="text-[11px] font-mono"
                  style={{ color: "rgba(255,255,255,.45)", background: "rgba(255,255,255,.06)", padding: "3px 9px", borderRadius: 6 }}
                >
                  #{jobData.job_number || jobData.id?.substring(0, 8).toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-2.5 mt-2 flex-wrap">
                {(() => {
                  const pill = statusPill[jobData.status] || { ink: "#22D3EE", bg: "rgba(14,116,144,.25)" };
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold"
                      style={{ color: pill.ink, background: pill.bg, padding: "4px 10px", borderRadius: 999 }}
                    >
                      <span className="rounded-full" style={{ width: 6, height: 6, background: pill.ink }} />
                      {statusLabels[jobData.status] || jobData.status}
                    </span>
                  );
                })()}
                {ws?.isConnected && (
                  <span
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold"
                    style={{ color: "#67C6B4", background: "rgba(103,198,180,.14)", padding: "4px 10px", borderRadius: 999 }}
                  >
                    <span className="rounded-full jc-pulse animate-pulse" style={{ width: 6, height: 6, background: "#67C6B4" }} />
                    Chat live
                  </span>
                )}
                <span className="flex items-center gap-1.5 text-sm" style={{ color: "rgba(255,255,255,.6)" }}>
                  <Calendar className="w-4 h-4" /> {formatDate(jobData.date)}
                  {jobData.hours > 0 && <span style={{ opacity: 0.85 }}>· {jobData.hours}h</span>}
                </span>
                {jobData.location_address && (
                  <span className="flex items-center gap-1.5 text-sm truncate" style={{ color: "rgba(255,255,255,.6)" }}>
                    <MapPin className="w-4 h-4 flex-shrink-0" /> {jobData.location_address}
                  </span>
                )}
              </div>
            </div>

            {/* Right cluster — crew avatars + primary action + menu */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {activeCrew.length > 0 && (
                <div className="hidden sm:flex items-center gap-2.5">
                  <div className="flex items-center">
                    {activeCrew.slice(0, 5).map((cm, i) =>
                      cm.profile_photo_url ? (
                        <img
                          key={i}
                          src={cm.profile_photo_url}
                          alt=""
                          className="rounded-full object-cover"
                          style={{ width: 34, height: 34, border: "2px solid #0C1120", marginLeft: i === 0 ? 0 : -10 }}
                        />
                      ) : (
                        <div
                          key={i}
                          className="rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ width: 34, height: 34, background: "linear-gradient(135deg,#572B7C,#28C0D0)", border: "2px solid #0C1120", marginLeft: i === 0 ? 0 : -10 }}
                        >
                          {(cm.hired_name || cm.hiredUserName || "?")[0]?.toUpperCase()}
                        </div>
                      )
                    )}
                  </div>
                  <span className="text-xs whitespace-nowrap" style={{ color: "rgba(255,255,255,.6)" }}>
                    {activeCrew.length} crew hired
                  </span>
                </div>
              )}

              {/* Primary action — Complete & release (desktop) */}
              {canRelease && (
                <button
                  onClick={handleReleaseFAB}
                  className="hidden lg:flex items-center gap-2.5 transition-all"
                  style={{
                    padding: "12px 20px",
                    borderRadius: 12,
                    background: "#15803D",
                    border: "none",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13.5,
                    cursor: "pointer",
                    boxShadow: "0 12px 28px -12px rgba(21,128,61,.7)",
                  }}
                >
                  <Star className="w-4 h-4" /> Complete &amp; release
                </button>
              )}

              <button
                type="button"
                aria-label="More options"
                className="hidden sm:flex items-center justify-center transition-colors"
                style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.7)" }}
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Progress Stepper */}
          <div className="sz-steps flex items-center mt-4 overflow-x-auto" style={{ paddingBottom: 4 }}>
            {steps.map((s, i) => (
              <Fragment key={s.label}>
                <div className="flex items-center gap-2.5 flex-shrink-0" style={{ opacity: s.state === "pending" ? 0.5 : 1 }}>
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 26, height: 26, borderRadius: 999,
                      ...(s.state === "complete"
                        ? { background: "#15803D" }
                        : s.state === "current"
                        ? { background: "rgba(40,192,208,.18)", border: "2px solid #28C0D0" }
                        : { background: "rgba(255,255,255,.08)", border: "2px dashed rgba(255,255,255,.22)", color: "rgba(255,255,255,.5)" }),
                    }}
                  >
                    {s.state === "complete" ? (
                      <Check className="w-3.5 h-3.5" style={{ color: "#fff" }} strokeWidth={3} />
                    ) : s.state === "current" ? (
                      <span className="rounded-full jc-pulse animate-pulse" style={{ width: 8, height: 8, background: "#28C0D0" }} />
                    ) : (
                      <DollarSign className="w-3 h-3" />
                    )}
                  </span>
                  <div style={{ lineHeight: 1.2 }}>
                    <div style={{ fontSize: 12.5, fontWeight: s.state === "current" ? 700 : 600, color: s.state === "current" ? "#28C0D0" : "#fff" }}>{s.label}</div>
                    <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.45)" }}>{s.sub}</div>
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <span
                    style={{
                      flex: 1, minWidth: 28, height: 2, margin: "0 12px",
                      background:
                        steps[i].state === "complete" && steps[i + 1].state === "complete"
                          ? "#15803D"
                          : steps[i].state === "complete"
                          ? "linear-gradient(90deg,#15803D,#28C0D0)"
                          : "rgba(255,255,255,.12)",
                    }}
                  />
                )}
              </Fragment>
            ))}
          </div>
          <div style={{ height: 14 }} />
        </div>

        {/* 3-column body — stacks at 1080px via scoped jc-body rule */}
        <div className="sz-col-mobile jc-body">
          {/* Left — Job Details (escrow hero + child) */}
          <div
            className="sz-panel-m flex flex-col"
            style={{ flex: "0 0 320px", borderRight: "1px solid rgba(255,255,255,.07)", overflowY: "auto" }}
          >
            <div style={{ padding: "20px 22px 0" }}>{escrowHero}</div>
            <div className="flex-1">
              <JobCommsJobDetails job={jobData} userRole={userRole} compact />
            </div>
          </div>

          {/* Centre — Chat */}
          <div
            className="sz-chat-m flex flex-col"
            style={{ flex: 1, minWidth: 0, minHeight: 0, borderRight: "1px solid rgba(255,255,255,.07)" }}
          >
            <JobCommsChat jobId={jobId} conversations={conversations} userEmail={user.email} userId={user.id} />
          </div>

          {/* Right — Documents */}
          <div
            className="sz-panel-m flex flex-col"
            style={{ flex: "0 0 260px", overflowY: "auto" }}
          >
            <PanelHeader icon={FileText} label="Documents" badge={documents.length} />
            <div className="flex-1">
              <JobCommsDocuments jobId={jobId} initialDocuments={documents} userEmail={user.email} userId={user.id} userRole={userRole} compact />
            </div>
          </div>
        </div>
      </div>

      {/* Floating Release Payment FAB (mobile / tablet) */}
      {canRelease && (
        <button
          onClick={handleReleaseFAB}
          className="sz-fab lg:hidden fixed z-50 flex items-center gap-2.5 text-white font-bold transition-all active:scale-95"
          style={{
            bottom: 24,
            right: 24,
            padding: "14px 20px",
            borderRadius: 12,
            background: "#15803D",
            boxShadow: "0 12px 28px -12px rgba(21,128,61,.7)",
          }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(255,255,255,.2)" }}>
            <CheckCircle className="w-4 h-4" />
          </div>
          <div className="text-left hidden sm:block">
            <p className="text-sm font-bold">Complete &amp; release</p>
            <p className="text-[10px]" style={{ color: "rgba(255,255,255,.75)" }}>Review &amp; pay crew</p>
          </div>
          <DollarSign className="w-5 h-5 sm:hidden" />
        </button>
      )}

      {/* Crew Selector Modal (advanced jobs) */}
      {showCrewSelector && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(8,11,20,.72)", backdropFilter: "blur(5px)" }}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: "#12162A", border: "1px solid rgba(255,255,255,.12)", boxShadow: "0 40px 90px -30px rgba(0,0,0,.85)" }}>
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,.08)", background: "linear-gradient(120deg,#572B7C,#78287C)" }}>
              <div className="flex items-center gap-2 text-white">
                <Users className="w-5 h-5" />
                <h3 className="font-bold">Select Crew Member</h3>
              </div>
              <button onClick={() => setShowCrewSelector(false)} style={{ color: "rgba(255,255,255,.8)" }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-sm mb-3" style={{ color: "rgba(255,255,255,.6)" }}>
                Choose which crew member to review and release payment for:
              </p>
              {activeCrew.map((cm, i) => {
                const isPaid = cm.payment_released;
                return (
                  <button
                    key={i}
                    onClick={() => !isPaid && handleSelectCrewForReview(cm)}
                    disabled={isPaid}
                    className="w-full flex items-center gap-3 p-3 rounded-xl transition-all"
                    style={{
                      background: "rgba(255,255,255,.04)",
                      border: "1px solid rgba(255,255,255,.1)",
                      opacity: isPaid ? 0.6 : 1,
                      cursor: isPaid ? "not-allowed" : "pointer",
                    }}
                  >
                    {cm.profile_photo_url ? (
                      <img src={cm.profile_photo_url} alt="" className={`w-10 h-10 rounded-full object-cover ${isPaid ? "grayscale" : ""}`} />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                        style={{ background: "linear-gradient(135deg,#572B7C,#28C0D0)" }}
                      >
                        {(cm.hired_name || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-white">{cm.hired_name || "Crew"}</p>
                      <p className="text-xs" style={{ color: "rgba(255,255,255,.5)" }}>
                        {(cm.role || "Crew").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                        {cm.subtotal ? ` - $${cm.subtotal}` : ""}
                      </p>
                    </div>
                    {isPaid ? (
                      <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#67C6B4" }}>
                        <CheckCircle className="w-4 h-4" /> Paid
                      </span>
                    ) : (
                      <CheckCircle className="w-5 h-5" style={{ color: "#28C0D0" }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Complete Job Modal */}
      {showCompleteModal && jobData && (
        <CompleteJobModal
          isOpen={showCompleteModal}
          onClose={() => { setShowCompleteModal(false); setSelectedCrewForReview(null); }}
          job={{
            id: jobData.id,
            title: jobData.title,
            status: jobData.status,
            date: jobData.date,
            jobDate: jobData.date,
            isAdvanced: isAdvanced,
            crew_payout: selectedCrewForReview
              ? (selectedCrewForReview.subtotal || 0) * 0.98
              : activeCrew[0] ? (activeCrew[0].subtotal || 0) * 0.98 : 0,
            total_amount: selectedCrewForReview?.subtotal || activeCrew[0]?.subtotal || jobData.total_amount || 0,
            hiredCrew: selectedCrewForReview ? {
              name: selectedCrewForReview.hired_name,
              email: selectedCrewForReview.hired_email,
              userId: selectedCrewForReview.hired_user_id,
              role: selectedCrewForReview.role,
              subtotal: selectedCrewForReview.subtotal,
            } : activeCrew[0] ? {
              name: activeCrew[0].hired_name,
              email: activeCrew[0].hired_email,
              userId: activeCrew[0].hired_user_id,
              role: activeCrew[0].role,
              subtotal: activeCrew[0].subtotal,
            } : null,
            crewMember: selectedCrewForReview || activeCrew[0] || null,
          }}
          userEmail={user.email}
          userId={user.id}
          onSuccess={() => {
            setShowCompleteModal(false);
            setSelectedCrewForReview(null);
            fetchJobCommsDetail();
          }}
        />
      )}
    </div>
  );
};

export default JobCommsDetail;
